import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Send } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import LegsEditor, { emptyLeg, Field, inputCls } from "@/components/LegsEditor";
import BallparkEstimator from "@/components/BallparkEstimator";
import RouteMap from "@/components/RouteMap";
import { useApi, errMsg } from "@/lib/api";
import { useMe } from "@/context/MeContext";
import { CATEGORIES, TRIP_TYPES, isStaff } from "@/lib/format";
import { ClientTypeFields, clientTypeError } from "@/components/ClientTypeFields";
import { PageHero } from "@/components/PageHero";

export function legsFor(type, legs) {
  if (type === "one_way") return [legs[0]];
  if (type === "round_trip") {
    const a = legs[0];
    const b = legs[1] || { ...emptyLeg(), from_airport: a.to_airport, to_airport: a.from_airport, pax: a.pax };
    return [a, b];
  }
  return legs.length >= 2 ? legs : [legs[0], { ...emptyLeg(), from_airport: legs[0].to_airport }];
}

const ADD_ONS = [
  ["ground_transportation", "Ground transportation", "Car or van service at departure and/or arrival", "Pickup / drop-off address, vehicle type, number of passengers…"],
  ["catering", "Catering", "Meals and beverages on board", "Menu preferences, dietary needs, drinks…"],
];

function AddOns({ value, onChange }) {
  return (
    <div className="grid sm:grid-cols-2 gap-4 items-start" data-testid="trip-add-ons">
      {ADD_ONS.map(([key, title, sub, ph]) => (
        <div key={key} className="lux-card p-5" data-testid={`add-on-${key}`}>
          <label className="flex items-start gap-4 cursor-pointer">
            <Checkbox checked={!!value[key]} onCheckedChange={(c) => onChange({ ...value, [key]: !!c })} className="mt-0.5 h-5 w-5" data-testid={`add-on-${key}-checkbox`} />
            <span>
              <span className="block text-white font-medium">{title}</span>
              <span className="block text-xs text-zinc-400 mt-0.5">{sub}</span>
            </span>
          </label>
          {value[key] && (
            <Textarea className="bg-[#0d0f14] border-white/10 min-h-[80px] mt-4 text-sm" maxLength={500} autoFocus
              value={value[`${key}_details`]} onChange={(e) => onChange({ ...value, [`${key}_details`]: e.target.value })}
              placeholder={ph} data-testid={`add-on-${key}-details`} />
          )}
        </div>
      ))}
    </div>
  );
}

const MIRROR = [["from_airport", "to_airport"], ["to_airport", "from_airport"], ["pax", "pax"]];

// Keep the return leg in sync with leg 1 until the user edits a return field themselves.
export function mirrorReturn(prev, next) {
  const [a0, b0] = prev;
  const [a1, b1] = next;
  if (!b1 || b0 !== b1) return next;
  const b = { ...b1 };
  MIRROR.forEach(([bk, ak]) => {
    if (!b0[bk] || String(b0[bk]) === String(a0[ak])) b[bk] = a1[ak];
  });
  return [a1, b, ...next.slice(2)];
}

export default function NewTrip() {
  const api = useApi();
  const navigate = useNavigate();
  const { me } = useMe();
  const staff = isStaff(me);
  const [tripType, setTripType] = useState("one_way");
  const [legs, setLegs] = useState([emptyLeg()]);
  const [category, setCategory] = useState("any");
  const [client, setClient] = useState({ clientType: "", program: "", jetCategory: "" });
  const jetCard = client.clientType === "jet_card";
  const [addOns, setAddOns] = useState({ ground_transportation: false, catering: false, ground_transportation_details: "", catering_details: "" });
  const [budget, setBudget] = useState("");
  const [notes, setNotes] = useState("");
  const [brokers, setBrokers] = useState([]);
  const [brokerId, setBrokerId] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (staff) api.get("/brokers").then((r) => setBrokers(r.data));
  }, [api, staff]);

  const changeType = (t) => {
    setTripType(t);
    setLegs((l) => legsFor(t, l));
  };

  const onLegsChange = (next) => setLegs((prev) => (tripType === "round_trip" ? mirrorReturn(prev, next) : next));

  const submit = async (e) => {
    e.preventDefault();
    if (staff && !brokerId) return toast.error("Select the broker this request belongs to");
    const clientErr = clientTypeError(client);
    if (clientErr) return toast.error(clientErr);
    setSaving(true);
    try {
      const { data } = await api.post("/trips", {
        trip_type: tripType,
        legs: legs.map((l) => ({ ...l, pax: Number(l.pax) })),
        client_type: client.clientType,
        jet_card_program: jetCard ? client.program : null,
        jet_card_budget: jetCard && budget ? Number(budget) : null,
        aircraft_category: jetCard ? client.jetCategory : category === "any" ? null : category,
        ...addOns,
        notes,
        broker_id: staff ? brokerId : undefined,
      });
      toast.success(`Request ${data.ref} submitted. The flight department has been notified.`);
      navigate(`/trips/${data.id}`);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
    <PageHero testId="new-trip-hero">
      <p className="eyebrow mb-3">{staff ? "Open a request for a broker" : "Request a quote"}</p>
      <h1 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl text-white mb-3">Where are you flying?</h1>
      <p className="text-zinc-300 text-sm max-w-xl">
        Our flight department sources options from certified charter operators and ranks them by price. You'll get an email and an in-app alert as options arrive.
      </p>
    </PageHero>
    <form onSubmit={submit} className="max-w-4xl" data-testid="new-trip-page">

      {staff && (
        <div className="mb-8 max-w-md">
          <Field label="Broker">
            <Select value={brokerId} onValueChange={setBrokerId}>
              <SelectTrigger className={inputCls} data-testid="broker-select"><SelectValue placeholder="Select a broker" /></SelectTrigger>
              <SelectContent>
                {brokers.length === 0 && <div className="px-3 py-2 text-xs text-zinc-500">No brokers yet. Add one under Users.</div>}
                {brokers.map((b) => (
                  <SelectItem key={b.id} value={b.id} data-testid={`broker-option-${b.id}`}>{b.name}{b.title ? ` · ${b.title}` : ""}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        </div>
      )}

      <div className="mb-8">
        <ClientTypeFields value={client} onChange={setClient} />
      </div>

      <div className="flex gap-2 mb-6 flex-wrap" data-testid="trip-type-tabs">
        {TRIP_TYPES.map((t) => (
          <button type="button" key={t.value} onClick={() => changeType(t.value)} data-testid={`trip-type-${t.value}`}
            className={`frame-btn ${tripType === t.value ? "gold" : ""}`}>{t.label}</button>
        ))}
      </div>

      <RouteMap legs={legs} />
      <LegsEditor legs={legs} onChange={onLegsChange} tripType={tripType} />
      <div className="mt-4"><BallparkEstimator legs={legs} category={!jetCard && category !== "any" ? category : jetCard ? client.jetCategory : null} testId="new-trip-ballpark" /></div>

      {jetCard && (
        <div className="mt-8 max-w-sm">
          <Field label="Jet Card budget (optional)">
            <Input className={inputCls} type="number" min="1" step="0.01" value={budget} onChange={(e) => setBudget(e.target.value)} placeholder="e.g. 45000" data-testid="jet-budget-new-input" />
          </Field>
          <p className="text-[11px] text-zinc-500 mt-1">The client's price for this trip. Options that cost more will be flagged and you'll be emailed.</p>
        </div>
      )}
      {!jetCard && (
        <div className="mt-8">
          <Field label="Preferred aircraft category">
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className={inputCls} data-testid="category-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any category</SelectItem>
                {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
        </div>
      )}
      <div className="mt-8">
        <span className="block text-[0.6rem] tracking-[0.22em] uppercase text-zinc-500 mb-1.5">Add-ons (optional)</span>
        <AddOns value={addOns} onChange={setAddOns} />
      </div>
      <div className="mt-8">
        <Field label="Notes for the flight department">
          <Textarea className="bg-[#0d0f14] border-white/10 min-h-[110px]" value={notes} onChange={(e) => setNotes(e.target.value)}
            placeholder="Pets, Wi-Fi, budget, flexibility on times…" data-testid="trip-notes-input" />
        </Field>
      </div>
      <button type="submit" disabled={saving} className="frame-btn gold mt-10" data-testid="submit-trip-request-btn">
        <Send size={13} /> {saving ? "Submitting…" : "Submit request"}
      </button>
    </form>
    </div>
  );
}
